import { useCallback, useEffect, useState } from "react";

import { LANG_DISPLAY, SUPPORTED_DASHBOARD_LANGS, tFor } from "./strings";
import { getUser } from "../services/api";

// ----------------------------------------------------------------------
// useT — staff-facing language hook for the dashboard.
//
// Returns { lang, setLang, t, availableLangs } where:
//   lang           current language code (e.g. "en", "am")
//   setLang(code)  switch language and persist to localStorage
//   t(key)         lookup helper — falls back to English if missing
//   availableLangs the language codes this tenant has been configured
//                  for by the admin (via Settings page). Drives the
//                  language-switcher pill in the navbar.
//
// Initial language selection priority:
//   1. Previously persisted choice in localStorage
//      ("menulink_dashboard_lang")  — respects user preference
//      across sessions and devices (per-browser cache)
//   2. First language in the user's restaurant.supported_languages
//      (set by admin in the per-tenant Settings page)
//   3. "en" as last-resort default
//
// The language code is also written into <html lang="…"> so screen
// readers and the browser handle the page correctly.
// ----------------------------------------------------------------------

const LS_KEY = "menulink_dashboard_lang";

function pickInitialLang() {
  // 1) Persisted choice
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && SUPPORTED_DASHBOARD_LANGS.includes(saved)) return saved;
  } catch (e) { /* localStorage unavailable in some embed contexts */ }

  // 2) English-first default (ODRA Language Policy). English is always in
  //    the baseline, so it is a valid choice for every venue. A user who
  //    prefers another language switches once and it persists (step 1).
  return "en";
}

function getAvailableLangs() {
  const user = getUser();
  // Staff dashboard uses the STAFF language set; fall back to the guest
  // list, then country defaults (ODRA Language Policy: independent sets).
  const list = (Array.isArray(user?.staff_languages) && user.staff_languages.length)
    ? user.staff_languages
    : (Array.isArray(user?.supported_languages) ? user.supported_languages : null);
  if (list && list.length > 0) {
    // Keep order from the admin's config and filter to known codes.
    return list.filter((c) => SUPPORTED_DASHBOARD_LANGS.includes(c));
  }
  // No admin-set list yet — fall back to country defaults that match
  // the customer-app's logic. For dashboard we just show English so the
  // navbar isn't cluttered with toggles the tenant hasn't asked for.
  const country = (user?.country || "ET").toUpperCase();
  if (country === "ET") return ["en", "am", "om"];
  if (country === "NL") return ["en", "nl"];
  return ["en"];
}

// ----------------------------------------------------------------------
// Shared language store. useState inside the hook is PER-COMPONENT, so
// the navbar switching language used to leave already-mounted pages on
// the old language until a refresh. This module-level store notifies
// every subscribed component, so the whole dashboard re-renders on a
// language switch instantly.
// ----------------------------------------------------------------------
let _lang = pickInitialLang();
const _listeners = new Set();

function setGlobalLang(code) {
  if (!SUPPORTED_DASHBOARD_LANGS.includes(code) || code === _lang) return;
  _lang = code;
  try { localStorage.setItem(LS_KEY, code); } catch (e) { /* noop */ }
  try { document.documentElement.setAttribute("lang", code); } catch (e) { /* noop */ }
  _listeners.forEach((fn) => fn(code));
}

export function useT() {
  const [lang, setLangState] = useState(_lang);

  // Subscribe this component to global language changes.
  useEffect(() => {
    const fn = (code) => setLangState(code);
    _listeners.add(fn);
    // Catch a change that happened between initial render and effect.
    if (_lang !== lang) setLangState(_lang);
    return () => _listeners.delete(fn);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const setLang = useCallback((code) => setGlobalLang(code), []);

  const t = useCallback((key) => tFor(lang, key), [lang]);

  const availableLangs = getAvailableLangs();

  return { lang, setLang, t, availableLangs, LANG_DISPLAY };
}
